import React, { useEffect, useState } from 'react';
import StockChart from './components/StockChart';
import MetricSelector from './components/MetricSelector';
import TickerSearch from './components/TickerSearch';
import DataSummarySection from './components/DataSummarySection';
import TabNavigation from './components/TabNavigation';
import TabContent from './components/TabContent';
import LdpmHistoryPage from './components/LdpmHistoryPage';
import OpenInterestChangesPage from './components/OpenInterestChangesPage';
import { OptionsVolumeOverTimePage } from './components/OptionsVolumeOverTimePage';
import { OptionHistoryPage } from './components/OptionHistoryPage';

import { LoadingState, ErrorState } from './components/LoadingStates';
import { useStockData, useVolatilityData, useGammaData, useFlowPerStrikeData } from './hooks/useStockData';
import { getOptionsMetric as getOptionsMetricUtil } from './utils/optionsMetrics';
import { MetricKey, GammaMetricKey, FlowMetricKey } from './types/StockData';

type TabType = 'signals' | 'flow' | 'volatility' | 'gamma' | 'flowPerStrike';
type PageType = 'main' | 'ldpmHistory' | 'openInterest' | 'optionsVolume' | 'optionHistory';

const App: React.FC = () => {
  // Page navigation state
  const [currentPage, setCurrentPage] = useState<PageType>('main');
  
  // Basic state
  const [selectedMetrics, setSelectedMetrics] = useState<MetricKey[]>(['callVol']);
  const [currentTicker, setCurrentTicker] = useState<string>('TSLA');
  const [windowSize, setWindowSize] = useState<number>(1);
  const [activeSignalTab, setActiveSignalTab] = useState<TabType>('signals');
  
  // Gamma and Flow Per Strike metric selection
  const [selectedGammaMetric, setSelectedGammaMetric] = useState<GammaMetricKey>('netGamma');
  const [selectedFlowMetric, setSelectedFlowMetric] = useState<FlowMetricKey>('volume');
  
  // Custom hooks for data management
  const { data, loading, error, selectedDataPoint, setSelectedDataPoint, loadStockData } = useStockData(currentTicker, windowSize);
  const { volatilityData, volatilityLoading, loadVolatilityData } = useVolatilityData(currentTicker, activeSignalTab);
  const { gammaData, gammaLoading, loadGammaData } = useGammaData(currentTicker, activeSignalTab, selectedDataPoint, data);
  const { flowPerStrikeData, flowPerStrikeLoading, loadFlowPerStrikeData } = useFlowPerStrikeData(currentTicker, activeSignalTab, selectedDataPoint, data);

  // Theme (dark mode) state with persistence
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    try {
      const saved = localStorage.getItem('theme');
      if (saved === 'dark' || saved === 'light') return saved;
      const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
      return prefersDark ? 'dark' : 'light';
    } catch {
      return 'light';
    }
  });

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    try { localStorage.setItem('theme', theme); } catch {}
  }, [theme]);

  const toggleTheme = () => setTheme(prev => (prev === 'dark' ? 'light' : 'dark'));

  // Navigation handlers
  const handlePageChange = (page: PageType) => {
    setCurrentPage(page);
  };

  // Event handlers
  const handleTickerSearch = (ticker: string) => {
    setCurrentTicker(ticker);
    console.log(`Searching for ticker: ${ticker}`);
  };

  const handleMetricChange = (metrics: MetricKey[]) => {
    setSelectedMetrics(metrics);
  };

  const handleWindowSizeChange = (newWindowSize: number) => {
    setWindowSize(newWindowSize);
  };

  const handleGammaMetricChange = (metric: GammaMetricKey) => {
    setSelectedGammaMetric(metric);
  };

  const handleFlowMetricChange = (metric: FlowMetricKey) => {
    setSelectedFlowMetric(metric);
  };

  const handleDataPointClick = (dataPoint: any) => {
    setSelectedDataPoint(dataPoint);
  };

  const handleRefresh = () => {
    loadStockData(currentTicker, windowSize);
    if (activeSignalTab === 'volatility') {
      loadVolatilityData(currentTicker);
    }
    if (activeSignalTab === 'gamma') {
      loadGammaData(currentTicker);
    }
    if (activeSignalTab === 'flowPerStrike') {
      loadFlowPerStrikeData(currentTicker);
    }
  };

  const isAnyLoading = loading || volatilityLoading || gammaLoading || flowPerStrikeLoading;

  // Helper function for DataSummarySection compatibility
  const getOptionsMetric = (point: any, metricKey: MetricKey): number => {
    return getOptionsMetricUtil(point, metricKey);
  };

  // Render LDPM History page
  if (currentPage === 'ldpmHistory') {
    return (
      <div>
        {/* Navigation Header */}
        <div className="bg-white dark:bg-gray-800 shadow-sm border-b dark:border-gray-700">
          <div className="container mx-auto px-4 py-3">
            <div className="flex items-center justify-between">
              <h1 className="text-xl font-semibold text-gray-800 dark:text-gray-100">Trading Assistant</h1>
              <nav className="flex gap-4 items-center">
                <button
                  onClick={() => handlePageChange('main')}
                  className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-blue-600 transition-colors"
                >
                  Main Dashboard
                </button>
                <button
                  onClick={() => handlePageChange('ldpmHistory')}
                  className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 dark:bg-blue-900/30 rounded-md"
                >
                  LDPM History
                </button>
                <button
                  onClick={() => handlePageChange('openInterest')}
                  className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-blue-600 transition-colors"
                >
                  Open Interest Changes
                </button>
                <button
                  onClick={() => handlePageChange('optionsVolume')}
                  className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-blue-600 transition-colors"
                >
                  Options Volume
                </button>
                <button
                  onClick={() => handlePageChange('optionHistory')}
                  className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-blue-600 transition-colors"
                >
                  Option History
                </button>
                <button
                  onClick={toggleTheme}
                  className="ml-2 px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-100 hover:border-gray-400 dark:hover:border-gray-500"
                  title="Toggle theme"
                >
                  {theme === 'dark' ? '☀️ Light' : '🌙 Dark'}
                </button>
              </nav>
            </div>
          </div>
        </div>
        <LdpmHistoryPage />
      </div>
    );
  }

  // Render Open Interest Changes page
  if (currentPage === 'openInterest') {
    return (
      <div>
        {/* Navigation Header */}
        <div className="bg-white dark:bg-gray-800 shadow-sm border-b dark:border-gray-700">
          <div className="container mx-auto px-4 py-3">
            <div className="flex items-center justify-between">
              <h1 className="text-xl font-semibold text-gray-800 dark:text-gray-100">Trading Assistant</h1>
              <nav className="flex gap-4 items-center">
                <button
                  onClick={() => handlePageChange('main')}
                  className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-blue-600 transition-colors"
                >
                  Main Dashboard
                </button>
                <button
                  onClick={() => handlePageChange('ldpmHistory')}
                  className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-blue-600 transition-colors"
                >
                  LDPM History
                </button>
                <button
                  onClick={() => handlePageChange('openInterest')}
                  className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 dark:bg-blue-900/30 rounded-md"
                >
                  Open Interest Changes
                </button>
                <button
                  onClick={() => handlePageChange('optionsVolume')}
                  className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-blue-600 transition-colors"
                >
                  Options Volume
                </button>
                <button
                  onClick={() => handlePageChange('optionHistory')}
                  className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-blue-600 transition-colors"
                >
                  Option History
                </button>
                <button
                  onClick={toggleTheme}
                  className="ml-2 px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-100 hover:border-gray-400 dark:hover:border-gray-500"
                  title="Toggle theme"
                >
                  {theme === 'dark' ? '☀️ Light' : '🌙 Dark'}
                </button>
              </nav>
            </div>
          </div>
        </div>
        <OpenInterestChangesPage />
      </div>
    );
  }

  // Render Option History page
  if (currentPage === 'optionHistory') {
    return (
      <div>
        {/* Navigation Header */}
        <div className="bg-white dark:bg-gray-800 shadow-sm border-b dark:border-gray-700">
          <div className="container mx-auto px-4 py-3">
            <div className="flex items-center justify-between">
              <h1 className="text-xl font-semibold text-gray-800 dark:text-gray-100">Trading Assistant</h1>
              <nav className="flex gap-4 items-center">
                <button
                  onClick={() => handlePageChange('main')}
                  className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-blue-600 transition-colors"
                >
                  Main Dashboard
                </button>
                <button
                  onClick={() => handlePageChange('ldpmHistory')}
                  className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-blue-600 transition-colors"
                >
                  LDPM History
                </button>
                <button
                  onClick={() => handlePageChange('openInterest')}
                  className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-blue-600 transition-colors"
                >
                  Open Interest Changes
                </button>
                <button
                  onClick={() => handlePageChange('optionsVolume')}
                  className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-blue-600 transition-colors"
                >
                  Options Volume
                </button>
                <button
                  onClick={() => handlePageChange('optionHistory')}
                  className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 dark:bg-blue-900/30 rounded-md"
                >
                  Option History
                </button>
                <button
                  onClick={toggleTheme}
                  className="ml-2 px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-100 hover:border-gray-400 dark:hover:border-gray-500"
                  title="Toggle theme"
                >
                  {theme === 'dark' ? '☀️ Light' : '🌙 Dark'}
                </button>
              </nav>
            </div>
          </div>
        </div>
        <OptionHistoryPage />
      </div>
    );
  }

  // Render Options Volume Over Time page
  if (currentPage === 'optionsVolume') {
    return (
      <div>
        {/* Navigation Header */}
        <div className="bg-white dark:bg-gray-800 shadow-sm border-b dark:border-gray-700">
          <div className="container mx-auto px-4 py-3">
            <div className="flex items-center justify-between">
              <h1 className="text-xl font-semibold text-gray-800 dark:text-gray-100">Trading Assistant</h1>
              <nav className="flex gap-4 items-center">
                <button
                  onClick={() => handlePageChange('main')}
                  className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-blue-600 transition-colors"
                >
                  Main Dashboard
                </button>
                <button
                  onClick={() => handlePageChange('ldpmHistory')}
                  className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-blue-600 transition-colors"
                >
                  LDPM History
                </button>
                <button
                  onClick={() => handlePageChange('openInterest')}
                  className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-blue-600 transition-colors"
                >
                  Open Interest Changes
                </button>
                <button
                  onClick={() => handlePageChange('optionsVolume')}
                  className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 dark:bg-blue-900/30 rounded-md"
                >
                  Options Volume
                </button>
                <button
                  onClick={() => handlePageChange('optionHistory')}
                  className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-blue-600 transition-colors"
                >
                  Option History
                </button>
                <button
                  onClick={toggleTheme}
                  className="ml-2 px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-100 hover:border-gray-400 dark:hover:border-gray-500"
                  title="Toggle theme"
                >
                  {theme === 'dark' ? '☀️ Light' : '🌙 Dark'}
                </button>
              </nav>
            </div>
          </div>
        </div>
        <OptionsVolumeOverTimePage 
          ticker={currentTicker}
          onTickerChange={handleTickerSearch}
        />
      </div>
    );
  }

  // Loading and error states for main page
  if (loading) {
    return <LoadingState />;
  }

  if (error) {
    return <ErrorState error={error} onRetry={() => loadStockData(currentTicker, windowSize)} />;
  }

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 dark:text-gray-100">
      {/* Navigation Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b dark:border-gray-700">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-semibold text-gray-800 dark:text-gray-100">Trading Assistant</h1>
            <nav className="flex gap-4 items-center">
              <button
                onClick={() => handlePageChange('main')}
                className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 dark:bg-blue-900/30 rounded-md"
              >
                Main Dashboard
              </button>
              <button
                onClick={() => handlePageChange('ldpmHistory')}
                className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-blue-600 transition-colors"
              >
                LDPM History
              </button>
              <button
                onClick={() => handlePageChange('openInterest')}
                className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-blue-600 transition-colors"
              >
                Open Interest Changes
              </button>
              <button
                onClick={() => handlePageChange('optionsVolume')}
                className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-blue-600 transition-colors"
              >
                Options Volume
              </button>
              <button
                onClick={() => handlePageChange('optionHistory')}
                className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-blue-600 transition-colors"
              >
                Option History
              </button>
              <button
                onClick={toggleTheme}
                className="ml-2 px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-100 hover:border-gray-400 dark:hover:border-gray-500"
                title="Toggle theme"
              >
                {theme === 'dark' ? '☀️ Light' : '🌙 Dark'}
              </button>
            </nav>
          </div>
        </div>
      </div>
      <div className="container mx-auto px-4 py-8">
        <header className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-800 dark:text-gray-100 mb-2">
            Trading Assistant
          </h1>
          <p className="text-gray-600 dark:text-gray-300">
            Real-time stock data visualization and analysis
          </p>
        </header>

        <TickerSearch 
          onTickerSearch={handleTickerSearch}
          currentTicker={currentTicker}
          onRefresh={handleRefresh}
          isLoading={isAnyLoading}
        />

        {data.length > 0 ? (
          <>
            {/* Chart with compact header */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-8 border border-gray-100 dark:border-gray-700">
              <DataSummarySection 
                data={data}
                selectedDataPoint={selectedDataPoint}
                selectedMetric={selectedMetrics[0] || 'callVol'}
                getOptionsMetric={getOptionsMetric}
              />
              
              <StockChart
                data={data}
                selectedMetrics={selectedMetrics}
                onDataPointClick={handleDataPointClick}
                windowSize={windowSize}
                onWindowSizeChange={handleWindowSizeChange}
              />
            </div>

            <div className="mt-8">
              <TabNavigation 
                activeTab={activeSignalTab}
                selectedDataPoint={selectedDataPoint}
                onTabChange={setActiveSignalTab}
              />

              <TabContent 
                activeTab={activeSignalTab}
                selectedDataPoint={selectedDataPoint}
                volatilityData={volatilityData}
                volatilityLoading={volatilityLoading}
                gammaData={gammaData}
                gammaLoading={gammaLoading}
                selectedGammaMetric={selectedGammaMetric}
                onGammaMetricChange={handleGammaMetricChange}
                flowPerStrikeData={flowPerStrikeData}
                flowPerStrikeLoading={flowPerStrikeLoading}
                selectedFlowMetric={selectedFlowMetric}
                onFlowMetricChange={handleFlowMetricChange}
                data={data}
              />
            </div>
          </>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-600">No data available</p>
          </div>
        )}
        
        <div className="mt-8">
          <MetricSelector
            selectedMetrics={selectedMetrics}
            onMetricChange={handleMetricChange}
            windowSize={windowSize}
          />
        </div>

      </div>
    </div>
    
  );
};

export default App;
